import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, PhoneOff, MessageSquare, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * NoShowAlertsPanel — advisor-facing alert card that surfaces customers
 * who booked in today but never checked in and are now 4h+ past their
 * booked IN time.
 *
 * Steve's spec (Aug 2026):
 *   "If a customer doesn't check in on the day and is a no show we need
 *    to remind the advisor to contact the customer or we have an auto
 *    send sms that says 'you have missed your appointment today would
 *    you like to re book'."
 *
 * User picked option (a): on-screen alert only, advisor clicks a button
 * to send the SMS themselves. Dismiss removes the row from the alert
 * list (stamps `no_show_dismissed_at` server-side).
 */
export default function NoShowAlertsPanel({ onChanged, nav }) {
  const [state, setState] = useState({ loading: true, count: 0, jobs: [], threshold: 4 });
  const [busy, setBusy] = useState({});   // per-job button lock

  const load = useCallback(async () => {
    try {
      const r = await api.get("/jobs/no-shows");
      setState({
        loading: false,
        count: r.data.count || 0,
        jobs: r.data.jobs || [],
        threshold: r.data.threshold_hours || 4,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 2 minutes so a fresh no-show pops up without a hard refresh.
    const t = setInterval(load, 120_000);
    return () => clearInterval(t);
  }, [load]);

  const sendSms = async (job) => {
    setBusy((b) => ({ ...b, [job.id]: "sms" }));
    try {
      const r = await api.post(`/jobs/${job.id}/send-no-show-sms`);
      if (r.data?.ok) {
        toast.success(`No-show SMS sent to ${firstNameOf(job.customer_name)}`);
      } else if (r.data?.already) {
        toast.info("SMS already sent within the last 30 minutes");
      } else {
        toast.error(r.data?.error || "Couldn't send the SMS");
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS send failed");
    } finally {
      setBusy((b) => ({ ...b, [job.id]: null }));
    }
  };

  const dismiss = async (job) => {
    setBusy((b) => ({ ...b, [job.id]: "dismiss" }));
    try {
      await api.post(`/jobs/${job.id}/dismiss-no-show`);
      toast.success(`Cleared ${job.vehicle_reg || job.customer_name}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    } finally {
      setBusy((b) => ({ ...b, [job.id]: null }));
    }
  };

  if (state.loading) return null;
  if (state.count === 0) return null;

  return (
    <div
      className="card-default p-5 border-destructive/40 bg-destructive/[0.04]"
      data-testid="advisor-no-shows-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-destructive/15 text-destructive">
            <AlertTriangle className="w-4 h-4" />
          </span>
          <div>
            <h2 className="font-display font-bold text-base">
              No-shows — need a phone call
            </h2>
            <p className="text-xs text-muted-foreground">
              Booked in today but never checked in, and it&apos;s more than{" "}
              {state.threshold}h past their slot.
            </p>
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-wider text-destructive font-mono-plex font-bold"
          data-testid="advisor-no-shows-count"
        >
          {state.count} to chase
        </span>
      </div>

      <ul className="space-y-2 mt-3">
        {state.jobs.map((j) => {
          const hasSms = !!j.no_show_sms_sent_at;
          const busyKind = busy[j.id];
          return (
            <li
              key={j.id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2 rounded-md border border-destructive/25 bg-background hover:bg-destructive/[0.06] transition-colors"
              data-testid={`advisor-no-show-row-${j.id}`}
            >
              <button
                type="button"
                onClick={() => nav?.(`/jobs/${j.id}`)}
                className="flex-1 min-w-0 text-left"
                data-testid={`advisor-no-show-open-${j.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <PhoneOff className="w-3.5 h-3.5 text-destructive shrink-0" />
                  <span className="font-semibold text-sm truncate">
                    {j.customer_name || "—"}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono-plex">
                    {j.vehicle_reg}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-destructive font-mono-plex font-bold">
                    booked {j.appointment_time} · {formatOverdue(j.minutes_overdue)} late
                  </span>
                  {hasSms && (
                    <span
                      className="text-[10px] uppercase tracking-wider text-primary font-mono-plex font-bold flex items-center gap-1"
                      data-testid={`advisor-no-show-smsed-${j.id}`}
                    >
                      <Check className="w-3 h-3" /> SMS sent
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {j.customer_phone || "no phone on file"}
                  {j.vehicle_make ? ` · ${j.vehicle_make} ${j.vehicle_model || ""}` : ""}
                </div>
              </button>

              <div className="flex items-center gap-2 sm:shrink-0">
                <button
                  type="button"
                  onClick={() => sendSms(j)}
                  disabled={!!busyKind || !j.customer_phone}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-xs font-bold hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid={`advisor-no-show-sms-${j.id}`}
                  title={
                    j.customer_phone
                      ? "Send 'you missed your appointment today — would you like to rebook?' SMS"
                      : "No phone number on file"
                  }
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  {busyKind === "sms" ? "Sending…" : hasSms ? "Send again" : "Send SMS"}
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(j)}
                  disabled={!!busyKind}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-bold text-muted-foreground hover:bg-muted disabled:opacity-50 transition"
                  data-testid={`advisor-no-show-dismiss-${j.id}`}
                  title="Advisor has spoken to the customer — hide this alert"
                >
                  <X className="w-3.5 h-3.5" />
                  {busyKind === "dismiss" ? "…" : "Dismiss"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function firstNameOf(full) {
  const s = (full || "").trim().split(/\s+/)[0] || "the customer";
  return s;
}

function formatOverdue(mins) {
  const m = Number(mins) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}
